import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  opportunitiesData: [],
  editedRows: {}, // Object instead of Map for serialization: { opportunityId: { field: value } }
  loading: false,
  error: null,
  saving: false,
  refreshing: false,
  lastFetched: null,
};

export const salesOpportunitySlice = createSlice({
  name: 'salesOpportunities',
  initialState,
  reducers: {
    // Set all opportunities data
    setOpportunitiesData: (state, action) => {
      state.opportunitiesData = action.payload;
      state.lastFetched = Date.now();
      state.error = null;
    },

    // Add or update a single opportunity
    updateOpportunity: (state, action) => {
      const updatedOpportunity = action.payload;
      const index = state.opportunitiesData.findIndex(p => p.id === updatedOpportunity.id);
      if (index !== -1) {
        state.opportunitiesData[index] = { ...state.opportunitiesData[index], ...updatedOpportunity };
      } else {
        state.opportunitiesData.push(updatedOpportunity);
      }
    },

    // Track cell edit (before saving to API)
    trackCellEdit: (state, action) => {
      const { opportunityId, field, value } = action.payload;

      if (!state.editedRows[opportunityId]) {
        state.editedRows[opportunityId] = {};
      }

      state.editedRows[opportunityId][field] = value;

      // Update the local data immediately for UI responsiveness
      // Create a new array with updated object to maintain immutability properly
      const index = state.opportunitiesData.findIndex(p => p.id === opportunityId);
      if (index !== -1) {
        state.opportunitiesData[index] = {
          ...state.opportunitiesData[index],
          [field]: value
        };
      }
    },

    // Clear all edited rows after successful save
    clearEditedRows: (state) => {
      state.editedRows = {};
    },

    // Clear edited rows for a specific opportunity
    clearEditedRowsForOpportunity: (state, action) => {
      const opportunityId = action.payload;
      delete state.editedRows[opportunityId];
    },

    // Discard edits and revert to original data
    discardEdits: (state, action) => {
      const opportunityId = action.payload;

      if (opportunityId) {
        // Discard edits for specific opportunity
        delete state.editedRows[opportunityId];
      } else {
        // Discard all edits
        state.editedRows = {};
      }

      // Note: You may want to refetch from API to get original values
      // This just clears the edited tracking
    },

    // Set loading state
    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    // Set refreshing state
    setRefreshing: (state, action) => {
      state.refreshing = action.payload;
    },

    // Set saving state
    setSaving: (state, action) => {
      state.saving = action.payload;
    },

    // Set error
    setError: (state, action) => {
      state.error = action.payload;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Reset entire state
    resetSalesOpportunities: (state) => {
      return initialState;
    },
  },
});

export const {
  setOpportunitiesData,
  updateOpportunity,
  trackCellEdit,
  clearEditedRows,
  clearEditedRowsForOpportunity,
  discardEdits,
  setLoading,
  setRefreshing,
  setSaving,
  setError,
  clearError,
  resetSalesOpportunities,
} = salesOpportunitySlice.actions;

// Selectors
export const selectOpportunitiesData = (state) => state.salesOpportunities.opportunitiesData;
export const selectEditedRows = (state) => state.salesOpportunities.editedRows;
export const selectEditedRowsCount = (state) => Object.keys(state.salesOpportunities.editedRows).length;
export const selectLoading = (state) => state.salesOpportunities.loading;
export const selectRefreshing = (state) => state.salesOpportunities.refreshing;
export const selectSaving = (state) => state.salesOpportunities.saving;
export const selectError = (state) => state.salesOpportunities.error;
export const selectLastFetched = (state) => state.salesOpportunities.lastFetched;

export default salesOpportunitySlice.reducer;
